import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

export function FAQSection({ params = {}, eyebrow = "FAQ", heading, note, testid = "home-faq-section" }) {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(0);
  const key = JSON.stringify(params);

  useEffect(() => {
    api.get("/faqs", { params }).then((r) => setFaqs(r.data)).catch(() => setFaqs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!faqs.length) return null;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <section className="border-y-2 border-black bg-white" data-testid={testid}>
      {/* Server-safe JSON-LD for rich FAQ snippets in Google */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-8 lg:grid-cols-[minmax(0,340px)_1fr] lg:py-28">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.4em] text-[#1e40af]">{eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-black sm:text-4xl">
            {heading || (<>Questions.<br />Answered.</>)}
          </h2>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-zinc-600">
            {note || "Still not sure? Slide in our DMs on Instagram — we reply in minutes."}
          </p>
        </div>

        <ul className="divide-y-2 divide-black border-y-2 border-black">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <li key={f.id} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                  className="flex w-full items-center justify-between gap-4 py-6 text-left transition-colors duration-200 hover:bg-zinc-50"
                >
                  <h3 className="m-0 font-display text-base font-bold text-black sm:text-lg">{f.question}</h3>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-black transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <p className="pb-6 pr-8 text-sm leading-relaxed text-zinc-700" data-testid={`faq-answer-${i}`}>
                    {f.answer}
                  </p>
                </motion.div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
